import { supabase } from './supabaseService';

// Fetch global leaderboard (paged)
export async function fetchGlobalLeaderboardPaged(timeframe: 'weekly' | 'alltime', limit: number, offset: number) {
  const view = timeframe === 'weekly' ? 'weekly_global_leaderboard' : 'alltime_global_leaderboard';
  const { data, error } = await supabase
    .from(view)
    .select('*')
    .order('total_score', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return data;
}

// Fetch global leaderboard (legacy, no pagination)
export async function fetchGlobalLeaderboard() {
  const { data, error } = await supabase
    .from('leaderboard_global')
    .select('*')
    .order('rank', { ascending: true });
  if (error) throw error;
  return data;
}

// Fetch church leaderboard
export async function fetchChurchLeaderboard(church: string) {
  const { data, error } = await supabase
    .from('leaderboard_church')
    .select('*')
    .eq('church', church)
    .order('rank_in_church', { ascending: true });
  if (error) throw error;
  return data;
}

// Fetch family leaderboard
export async function fetchFamilyLeaderboard(family: string) {
  const { data, error } = await supabase
    .from('leaderboard_family')
    .select('*')
    .eq('family', family)
    .order('rank_in_family', { ascending: true });
  if (error) throw error;
  return data;
}

// Fetch weekly leaderboard (legacy, no pagination)
export async function fetchWeeklyLeaderboard() {
  const { data, error } = await supabase
    .from('leaderboard_weekly')
    .select('*')
    .order('weekly_rank', { ascending: true });
  if (error) throw error;
  return data;
}

// Fetch all-time leaderboard (legacy, no pagination)
export async function fetchAllTimeLeaderboard() {
  const { data, error } = await supabase
    .from('leaderboard_alltime')
    .select('*')
    .order('alltime_rank', { ascending: true });
  if (error) throw error;
  return data;
}

// Fetch family leaderboard (paged)
export async function fetchFamilyLeaderboardPaged(familyId: string, timeframe: 'weekly' | 'alltime', limit: number, offset: number) {
  const view = timeframe === 'weekly' ? 'weekly_families_leaderboard' : 'alltime_families_leaderboard';
  const scoreColumn = timeframe === 'weekly' ? 'weekly_score' : 'total_score';
  const { data, error } = await supabase
    .from(view)
    .select('*')
    // .eq('family_id', familyId) // enable if scoping to a single family
    .range(offset, offset + limit - 1)
    .order(scoreColumn, { ascending: false });
  if (error) throw error;
  return data;
}

// Fetch church leaderboard (paged)
export async function fetchChurchLeaderboardPaged(churchId: string, timeframe: 'weekly' | 'alltime', limit: number, offset: number) {
  const view = timeframe === 'weekly' ? 'weekly_churches_leaderboard' : 'alltime_churches_leaderboard';
  const { data, error } = await supabase
    .from(view)
    .select('*')
    // .eq('church_id', churchId) // Uncomment if church_id is a column in the view
    .range(offset, offset + limit - 1)
    .order('total_score', { ascending: false });
  if (error) throw error;
  return data;
}

// Fetch teams leaderboard (paged)
export async function fetchTeamsLeaderboardPaged(teamId: string, timeframe: 'weekly' | 'alltime', limit: number, offset: number) {
  const view = timeframe === 'weekly' ? 'weekly_teams_leaderboard' : 'alltime_teams_leaderboard';
  const { data, error } = await supabase
    .from(view)
    .select('*')
    // .eq('team_id', teamId) // Uncomment if team_id is a column in the view
    .range(offset, offset + limit - 1)
    .order('total_score', { ascending: false });
  if (error) throw error;
  return data;
}

// Fetch friends leaderboard (paged, via RPC)

// Fetch friends leaderboard (RPC)
// Fetch friends leaderboard (paged convenience wrapper)
export async function fetchFriendsLeaderboardPaged(userId: string, timeframe: 'weekly' | 'alltime', limit: number, offset: number) {
  // Current RPC returns full set; we slice client-side until pagination support is added server-side
  const data = await fetchFriendsLeaderboard(userId, timeframe);
  return data.slice(offset, offset + limit);
}

export async function fetchFriendsLeaderboard(userId: string, timeframe: 'weekly' | 'alltime' = 'alltime') {
  const { data, error } = await supabase.rpc('get_friends_leaderboard', {
    p_user_id: userId,
    p_timeframe: timeframe,
  });
  if (error) throw error;
  return data;
}

// DIAGNOSTIC: Test XP sync between views and direct queries
export async function diagnoseXPSync(userId: string) {
  try {
    console.log('🔍 Diagnosing XP sync issue...');
    
    // 1. Get user XP directly from user_xp table
    const { data: directXP, error: directError } = await supabase
      .from('user_xp')
      .select('total_xp, current_level, rank')
      .eq('user_id', userId)
      .single();
    
    if (directError) {
      console.error('❌ Error fetching direct XP:', directError);
      return { error: directError.message };
    }
    
    // 2. Get user XP from global leaderboard view
    const { data: viewXP, error: viewError } = await supabase
      .from('alltime_global_leaderboard')
      .select('total_score, level, rank')
      .eq('user_id', userId)
      .single();
    
    // 3. Compare results
    const diagnosis = {
      directXP: directXP?.total_xp || 0,
      viewXP: viewXP?.total_score || 0,
      directLevel: directXP?.current_level || 1,
      viewLevel: viewXP?.level || 1,
      directRank: directXP?.rank || 'Beginner',
      viewRank: viewXP?.rank || 'Unknown',
      syncIssue: (directXP?.total_xp || 0) !== (viewXP?.total_score || 0),
      viewError: viewError?.message || null
    };
    
    console.log('📊 XP Sync Diagnosis:', diagnosis);
    return diagnosis;
    
  } catch (error) {
    console.error('❌ Diagnosis failed:', error);
    return { error: (error as Error).message };
  }
}

// SOLUTION: Direct XP leaderboard query (bypasses potentially stale views)
export async function fetchGlobalLeaderboardDirect(limit: number = 50, offset: number = 0) {
  try {
    console.log('🚀 Fetching leaderboard directly from user_xp...');
    
    const { data, error } = await supabase
      .from('user_xp')
      .select(`
        user_id,
        total_xp,
        current_level,
        rank,
        user_profiles!inner(
          display_name,
          username,
          avatar_url
        )
      `)
      .order('total_xp', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      console.error('❌ Error fetching direct leaderboard:', error);
      throw error;
    }
    
    // Transform to match expected leaderboard format
    const transformedData = data?.map((entry, index) => {
      const profile = entry.user_profiles as any; // Type assertion for joined profile
      return {
        user_id: entry.user_id,
        total_score: entry.total_xp,
        level: entry.current_level,
        rank: index + 1 + offset,
        display_name: profile?.display_name,
        username: profile?.username,
        avatar_url: profile?.avatar_url,
        name: profile?.display_name || profile?.username
      };
    }) || [];
    
    console.log('✅ Direct leaderboard fetched:', transformedData.length, 'entries');
    return transformedData;
    
  } catch (error) {
    console.error('❌ Direct leaderboard fetch failed:', error);
    throw error;
  }
}
